import '../styles/GlobalDesign.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminCreditManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [depositsRes, resellersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/deposits`, { headers }),
        axios.get(`${API_URL}/admin/resellers`, { headers })
      ]);

      setDeposits(depositsRes.data.deposits);
      setResellers(resellersRes.data.resellers);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setLoading(false);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleCreditSystem = async (resellerId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const action = newStatus === 1 ? 'activer' : 'désactiver';
    
    if (!window.confirm(`Voulez-vous ${action} le système de crédit pour ce revendeur ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/resellers/${resellerId}/credit-system`,
        { credit_system_enabled: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Système de crédit ${action === 'activer' ? 'activé' : 'désactivé'} avec succès`);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleApproveDeposit = async (depositId) => {
    if (!window.confirm('Approuver ce dépôt ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/deposits/${depositId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Dépôt approuvé avec succès');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de l\'approbation');
    }
  };

  const handleRejectDeposit = async (depositId) => {
    const reason = prompt('Raison du rejet (optionnel):');
    if (reason === null) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/deposits/${depositId}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Dépôt rejeté');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors du rejet');
    }
  };

  const handleAdjustCredit = async (e) => {
    e.preventDefault();
    
    if (!selectedReseller || !adjustmentAmount) {
      alert('Veuillez sélectionner un revendeur et entrer un montant');
      return;
    }

    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount === 0) {
      alert('Montant invalide');
      return;
    }

    if (!window.confirm(`Ajuster le crédit de ${amount} FCFA pour ${selectedReseller.nom} ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/resellers/${selectedReseller.id}/adjust-credit`,
        { amount, description: adjustmentDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Crédit ajusté avec succès');
      setAdjustmentAmount('');
      setAdjustmentDescription('');
      setSelectedReseller(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de l\'ajustement');
    }
  };

  const filteredDeposits = filterStatus === 'all' 
    ? deposits 
    : deposits.filter(d => d.status === filterStatus);

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-page-wrapper" style={{ textAlign: 'center' }}>Chargement...</div>
      </AdminLayout>
    );
  }

  const pendingDeposits = deposits.filter(d => d.status === 'pending');

  return (
    <AdminLayout>
    <div className="admin-page-wrapper">
      <h1 className="content-title" style={{ fontSize: '2rem', marginBottom: '2rem' }}>Gestion du Système de Crédit</h1>

      {/* Statistiques rapides */}
      <div className="admin-stats-row">
        <div className="admin-stat-tile accent-1">
          <div className="tile-label">Dépôts en attente</div>
          <div className="tile-value">
            {pendingDeposits.length}
          </div>
        </div>

        <div className="admin-stat-tile accent-2">
          <div className="tile-label">Montant total en attente</div>
          <div className="tile-value">
            {pendingDeposits.reduce((sum, d) => sum + d.amount, 0).toLocaleString()} FCFA
          </div>
        </div>

        <div className="admin-stat-tile accent-3">
          <div className="tile-label">Revendeurs avec crédit actif</div>
          <div className="tile-value">
            {resellers.filter(r => r.credit_system_enabled === 1).length}
          </div>
        </div>
      </div>

      {/* Ajustement manuel de crédit */}
      <div className="admin-section-card">
        <h2 className="section-title">⚙️ Ajustement Manuel de Crédit</h2>
        <form onSubmit={handleAdjustCredit} className="admin-form-2col">
          <div>
            <label className="form-label">Revendeur</label>
            <select
              value={selectedReseller?.id || ''}
              onChange={(e) => setSelectedReseller(resellers.find(r => r.id === parseInt(e.target.value)))}
              required
            >
              <option value="">Sélectionner un revendeur</option>
              {resellers.map(r => (
                <option key={r.id} value={r.id}>
                  {r.nom} ({r.slug}) - Crédit actuel: {r.credit_balance || 0} FCFA
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">
              Montant (positif = ajout, négatif = retrait)
            </label>
            <input
              type="number"
              value={adjustmentAmount}
              onChange={(e) => setAdjustmentAmount(e.target.value)}
              placeholder="Ex: 5000 ou -2000"
              required
            />
          </div>

          <div className="admin-form-2col-full">
            <label className="form-label">Description</label>
            <input
              type="text"
              value={adjustmentDescription}
              onChange={(e) => setAdjustmentDescription(e.target.value)}
              placeholder="Raison de l'ajustement"
            />
          </div>

          <div className="admin-form-2col-full">
            <button
              type="submit"
              className="new-design-btn new-design-btn-primary"
            >
              Ajuster le crédit
            </button>
          </div>
        </form>
      </div>

      {/* Liste des dépôts */}
      <div className="admin-section-card">
        <div className="section-header-row">
          <h2 className="section-title">💳 Demandes de Dépôt</h2>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvés</option>
            <option value="rejected">Rejetés</option>
          </select>
        </div>

        {filteredDeposits.length === 0 ? (
          <div className="empty-state">
            Aucun dépôt trouvé
          </div>
        ) : (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Revendeur</th>
                  <th>Montant</th>
                  <th>Méthode</th>
                  <th>Référence</th>
                  <th>Statut</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeposits.map(deposit => (
                  <tr key={deposit.id}>
                    <td>
                      {new Date(deposit.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ fontWeight: '500' }}>{deposit.reseller_name}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>{deposit.reseller_email}</div>
                    </td>
                    <td className="tx-credit" style={{ textAlign: 'right' }}>
                      {deposit.amount.toLocaleString()} FCFA
                    </td>
                    <td>{deposit.payment_method}</td>
                    <td style={{ fontSize: '0.9rem', color: 'var(--gray-400)' }}>
                      {deposit.payment_reference || '-'}
                    </td>
                    <td>
                      <span className={
                        deposit.status === 'pending' ? 'badge-pending' :
                        deposit.status === 'approved' ? 'badge-approved' : 'badge-rejected'
                      }>
                        {deposit.status === 'pending' ? 'En attente' : 
                         deposit.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {deposit.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            className="btn-approve"
                            onClick={() => handleApproveDeposit(deposit.id)}
                          >
                            ✓ Approuver
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => handleRejectDeposit(deposit.id)}
                          >
                            ✗ Rejeter
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Liste des revendeurs avec toggle */}
      <div className="admin-section-card">
        <h2 className="section-title">👥 Revendeurs - Système de Crédit</h2>

        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Revendeur</th>
                <th style={{ textAlign: 'right' }}>Solde Crédit</th>
                <th style={{ textAlign: 'right' }}>Solde Commission</th>
                <th style={{ textAlign: 'center' }}>Système de Crédit</th>
              </tr>
            </thead>
            <tbody>
              {resellers.map(reseller => (
                <tr key={reseller.id}>
                  <td>
                    <div style={{ fontWeight: '500' }}>{reseller.nom}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>{reseller.slug}</div>
                  </td>
                  <td className="tx-credit" style={{ textAlign: 'right' }}>
                    {(reseller.credit_balance || 0).toLocaleString()} FCFA
                  </td>
                  <td className="tx-credit" style={{ textAlign: 'right' }}>
                    {(reseller.balance || 0).toLocaleString()} FCFA
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <label style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      position: 'relative'
                    }}>
                      <input
                        type="checkbox"
                        checked={reseller.credit_system_enabled === 1}
                        onChange={() => handleToggleCreditSystem(reseller.id, reseller.credit_system_enabled)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '50px',
                        height: '26px',
                        background: reseller.credit_system_enabled === 1 ? '#10b981' : '#374151',
                        borderRadius: '13px',
                        position: 'relative',
                        transition: 'background 0.3s'
                      }}>
                        <div style={{
                          width: '22px',
                          height: '22px',
                          background: 'white',
                          borderRadius: '50%',
                          position: 'absolute',
                          top: '2px',
                          left: reseller.credit_system_enabled === 1 ? '26px' : '2px',
                          transition: 'left 0.3s',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminCreditManagement;
